import { useEffect, useRef, useState } from "react";
import TextField from "@mui/material/TextField";
import { evaluate } from "mathjs/number";

import i18n from "../../i18n";

interface MathInputProps {
  value: number;
  disabled?: boolean;
  onValueChange: (val: number) => void;
  onBlur?: (finalValue: number) => void | Promise<void>;
  step?: number;
  /** Debounce delay (ms) applied to the +/- buttons. The typed-blur save is not debounced. */
  stepDebounceMs?: number;
  className?: string;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  inputRef?: React.Ref<HTMLInputElement>;
}

const MathInput: React.FC<MathInputProps> = ({
  value,
  disabled = false,
  onValueChange,
  onBlur,
  step = 1,
  stepDebounceMs = 500,
  className,
  onKeyDown,
  inputRef
}) => {
  const [expr, setExpr] = useState<string | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);
  const stepTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingValueRef = useRef(value);

  useEffect(() => {
    if (stepTimerRef.current == null) pendingValueRef.current = value;
  }, [value]);

  useEffect(() => () => {
    if (stepTimerRef.current) clearTimeout(stepTimerRef.current);
  }, []);

  const isLocked = disabled || isSaving;

  const resolvePendingExpr = (): number => {
    if (expr == null) return value;
    if (expr === "") return 0;
    try {
      const result = evaluate(expr);
      if (typeof result === "number" && !isNaN(result)) {
        return Math.round(result);
      }
    } catch {
      // ignore invalid expression
    }
    return value;
  };

  const handleBlur = async () => {
    const hadPendingStep = stepTimerRef.current != null;
    if (stepTimerRef.current) {
      clearTimeout(stepTimerRef.current);
      stepTimerRef.current = null;
    }

    let finalValue = hadPendingStep ? pendingValueRef.current : value;

    if (expr === "") {
      finalValue = 0;
      setExpr("0");
      onValueChange(finalValue);
    } else if (expr != null) {
      try {
        const result = evaluate(expr);
        if (typeof result === "number" && !isNaN(result)) {
          finalValue = Math.round(result);
          onValueChange(finalValue);
        }
      } catch {
        // ignore invalid expression
      }
      setExpr(undefined);
    }

    setIsSaving(true);
    try {
      await onBlur?.(finalValue);
    } finally {
      setIsSaving(false);
    }
  };

  const applyStep = (delta: number) => {
    // Note: the below is to allow rapid typing of the +/- keys without problems with the debounce
    const base = stepTimerRef.current != null ? pendingValueRef.current : resolvePendingExpr();
    const next = base + delta;

    setExpr(undefined);
    onValueChange(next);
    pendingValueRef.current = next;

    if (stepTimerRef.current) clearTimeout(stepTimerRef.current);
    stepTimerRef.current = setTimeout(async () => {
      stepTimerRef.current = null;
      setIsSaving(true);
      try {
        await onBlur?.(pendingValueRef.current);
      } finally {
        setIsSaving(false);
      }
    }, stepDebounceMs);
  };

  const stepperButtonClasses =
    "px-2 flex items-center justify-center text-theme-text bg-theme-surface-2 hover:bg-theme-surface disabled:bg-theme-surface-2 disabled:cursor-not-allowed transition select-none";

  return (
    <div className={`flex items-stretch ${className ?? ""}`}>
      <button
        type="button"
        disabled={isLocked}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => applyStep(-step)}
        className={`${stepperButtonClasses}`}
        aria-label={i18n.t("mathInput.decrement")}
      >
        −
      </button>
      <TextField
        value={expr ?? value.toString()}
        disabled={isLocked}
        size="small"
        inputRef={inputRef}
        onBlur={handleBlur}
        className="flex-1"
        slotProps={{
          input: {
            onKeyDown,
            className: "!rounded-none"
          },
          root: {
            sx: {
              "& .MuiOutlinedInput-notchedOutline": {
                borderTop: "none",
                borderBottom: "none"
              }
            }
          }
        }}
        onChange={(e) => {
          if (!e.target.value) {
            setExpr("");
            return;
          }

          const raw = e.target.value;
          if (/^[0-9+\-*/().\s]*$/.test(raw)) {
            setExpr(raw);
          }
        }}
      />
      <button
        type="button"
        disabled={isLocked}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => applyStep(step)}
        className={`${stepperButtonClasses}`}
        aria-label={i18n.t("mathInput.increment")}
      >
        +
      </button>
    </div>
  );
};

export default MathInput;
